/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTheme, ThemeMode } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';

// SVG icon paths (Heroicons outline style)
const icons = {
  chat: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  folder: 'M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z',
  chartBar: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z',
  users: 'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z',
  megaphone: 'M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46',
  lightbulb: 'M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18',
  sun: 'M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z',
  moon: 'M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z',
  desktop: 'M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25',
  question: 'M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z',
  phone: 'M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z',
  chevronRight: 'M8.25 4.5l7.5 7.5-7.5 7.5',
  chevronLeft: 'M15.75 19.5L8.25 12l7.5-7.5',
  logout: 'M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75',
  heart: 'M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z',
};

// SVG Icon component
interface IconProps {
  path: string;
  className?: string;
  strokeWidth?: number;
}

const Icon: React.FC<IconProps> = ({ path, className = 'w-5 h-5', strokeWidth = 1.5 }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={strokeWidth}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

/**
 * Sidebar component for BrandHealth 360
 * Includes theme-aware styling and theme toggle
 * Uses SVG icons matching trends-2 pattern
 */
const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const { mode, setMode, isDark } = useTheme();
  const { user: auth0User, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const toggleSidebar = () => setCollapsed(!collapsed);

  // Navigation items for brand health
  const navItems = [
    { id: 'chat', path: '/chat', icon: icons.chat, label: 'Brand Doctor', tooltip: 'AI-powered brand health diagnosis' },
    { id: 'dashboard', path: '/dashboard', icon: icons.chartBar, label: 'Dashboard', tooltip: 'View brand health metrics' },
    { id: 'insights', path: '/insights', icon: icons.lightbulb, label: 'Insights', tooltip: 'AI-driven brand insights' },
    { id: 'segments', path: '/segments', icon: icons.users, label: 'Personas', tooltip: 'Analyze customer personas' },
    { id: 'promotions', path: '/promotions', icon: icons.megaphone, label: 'Promotions', tooltip: 'Track promotion performance' }
  ];

  // Get user info from Auth0
  const user = {
    name: auth0User?.name || auth0User?.email || 'User',
    email: auth0User?.email || '',
    picture: auth0User?.picture || null
  };

  // Theme toggle options
  const themeOptions: { mode: ThemeMode; icon: string; label: string }[] = [
    { mode: 'light', icon: icons.sun, label: 'Light' },
    { mode: 'dark', icon: icons.moon, label: 'Dark' },
    { mode: 'system', icon: icons.desktop, label: 'System' }
  ];

  const currentThemeIcon = mode === 'system' ? icons.desktop : mode === 'dark' ? icons.moon : icons.sun;

  const cycleTheme = () => {
    const nextMode = mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system';
    setMode(nextMode);
  };

  return (
    <div
      className={`relative z-sticky bg-background-secondary border-r border-border transition-all duration-300 h-full flex flex-col ${
        collapsed ? 'w-16' : 'w-56'
      }`}
      style={{ pointerEvents: 'auto' }}
    >
      {/* Logo and Title Section */}
      <div className="flex flex-col items-center pt-4 pb-3">
        {/* Logo - theme-aware */}
        <div className={`${collapsed ? 'w-10 h-10' : 'w-20 h-20'} relative flex items-center justify-center mb-0`}>
          <img
            src={collapsed
              ? (isDark ? "/icon.png" : "/icon-light.png")
              : (isDark ? "/sila-logo.png" : "/sila-light.png")}
            alt="Sila"
            className="w-full h-full object-contain"
          />
          {/* Beta badge */}
          {!collapsed && (
            <span className="absolute -top-1 -right-1 bg-yellow-400 text-black text-xs font-semibold px-1 py-0.5 rounded-full uppercase">
              beta
            </span>
          )}
        </div>

        {/* Title with gradient */}
        {!collapsed && (
          <div className="flex items-center justify-center -mt-1">
            <h1 className="text-base flex items-center">
              <span className="font-light tracking-tight text-text-secondary">Brand</span>
              <span className="font-medium text-gradient">Health</span>
              <span className="font-light tracking-tight text-text-secondary ml-1">360</span>
              {/* Heart icon */}
              <span className="ml-2">
                <Icon path={icons.heart} className="w-3 h-3 text-primary" strokeWidth={2} />
              </span>
            </h1>
          </div>
        )}
      </div>

      {/* Main Navigation */}
      <nav className="flex-grow px-2 py-2 overflow-y-auto">
        <div className="space-y-2">
          {navItems.map(item => (
            <NavLink
              key={item.id}
              to={item.path}
              className={({ isActive }) =>
                `w-full flex items-center ${collapsed ? 'justify-center' : 'justify-start'} py-2.5 px-3 rounded-lg transition-all duration-200 group relative ${
                  isActive
                    ? 'bg-primary/20 text-primary font-semibold shadow-sm'
                    : 'text-text-muted hover:text-foreground hover:bg-background-tertiary/50'
                }`
              }
              title={collapsed ? item.tooltip : undefined}
            >
              {({ isActive }) => (
                <>
                  {/* Active indicator - bottom pill when collapsed, left bar when expanded */}
                  {isActive && (
                    <div className={`absolute ${collapsed ? 'bottom-0 left-1/2 -translate-x-1/2 w-6 h-1' : 'left-0 top-1/2 -translate-y-1/2 w-1 h-6'} bg-primary rounded-full`} />
                  )}
                  <div className={`flex items-center justify-center ${!collapsed && isActive ? 'ml-1' : ''}`}>
                    <Icon
                      path={item.icon}
                      className="w-5 h-5"
                      strokeWidth={isActive ? 2 : 1.5}
                    />
                  </div>

                  {!collapsed && (
                    <span className={`ml-3 text-sm ${isActive ? 'font-medium' : ''}`}>{item.label}</span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Controls - Fixed at bottom */}
      <div className="mt-auto border-t border-border">
        {/* Theme Toggle */}
        <div className={`px-2 py-2 ${collapsed ? 'flex flex-col space-y-1' : ''}`}>
          {collapsed ? (
            <button
              className="w-full text-text-muted hover:text-foreground transition-colors p-2 rounded-lg hover:bg-background-tertiary flex justify-center items-center"
              onClick={cycleTheme}
              title={`Theme: ${mode}`}
            >
              <Icon path={currentThemeIcon} className="w-5 h-5" strokeWidth={1.5} />
            </button>
          ) : (
            <div className="flex items-center justify-between px-1 py-1">
              <span className="text-xs text-text-muted">Theme</span>
              <div className="flex bg-background-tertiary rounded-md p-0.5">
                {themeOptions.map(option => (
                  <button
                    key={option.mode}
                    onClick={() => setMode(option.mode)}
                    className={`p-1.5 rounded transition-all ${
                      mode === option.mode
                        ? 'bg-background text-foreground'
                        : 'text-text-muted hover:text-foreground'
                    }`}
                    title={option.label}
                  >
                    <Icon path={option.icon} className="w-3.5 h-3.5" strokeWidth={mode === option.mode ? 2 : 1.5} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Collapse Toggle */}
        <div className="px-2 py-2">
          <button
            className="w-full text-text-muted hover:text-foreground transition-colors p-2 rounded-lg hover:bg-background-tertiary flex items-center justify-center"
            onClick={toggleSidebar}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <Icon path={collapsed ? icons.chevronRight : icons.chevronLeft} className="w-4 h-4" strokeWidth={2} />
            {!collapsed && <span className="ml-2 text-sm">Collapse</span>}
          </button>
        </div>

        {/* User Section */}
        <div className={`px-2 py-3 border-t border-border ${collapsed ? 'flex flex-col items-center space-y-2' : ''}`}>
          {/* User Profile */}
          {collapsed ? (
            <div
              className="w-8 h-8 rounded-full bg-text-muted/20 flex items-center justify-center cursor-pointer"
              title={user.name}
            >
              <span className="text-foreground text-xs font-medium">
                {user.name.charAt(0)}
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-text-muted/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-foreground text-xs font-medium">
                    {user.name.charAt(0)}
                  </span>
                </div>
                <div className="ml-2 overflow-hidden min-w-0">
                  <div className="text-foreground truncate text-sm">{user.name}</div>
                </div>
              </div>
              <button
                className="text-text-muted hover:text-foreground transition-colors p-1.5 rounded hover:bg-background-tertiary"
                title="Sign out"
                onClick={logout}
              >
                <Icon path={icons.logout} className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          )}

          {collapsed && (
            <button
              className="text-text-muted hover:text-foreground transition-colors p-1.5 rounded hover:bg-background-tertiary"
              title="Sign out"
              onClick={() => console.log('Logout clicked')}
            >
              <Icon path={icons.logout} className="w-4 h-4" strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
